import React, { useEffect, useMemo, useState } from "react"
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from "@mui/material"
import { alpha, useTheme } from "@mui/material/styles"
import { formatCentsWithSymbol } from "../utils/formatCurrency"
import { useTranslation } from "react-i18next"

type CashFundCountDialogProps = {
    open: boolean
    theoreticalMinor: number
    chequeTheoreticalMinor?: number
    chequeTheoreticalCount?: number
    currency?: string
    loading?: boolean
    onCancel: () => void
    onConfirm: (countedMinor: number) => void | Promise<void>
}

function parseAmountToMinor(input: string): number | null {
    const normalized = String(input || "")
        .replace(",", ".")
        .trim()
    if (!normalized) {
        return null
    }

    const parsed = Number.parseFloat(normalized)
    if (!Number.isFinite(parsed) || parsed < 0) {
        return null
    }

    return Math.round(parsed * 100)
}

function parseCountToInteger(input: string): number | null {
    const normalized = String(input || "").trim()
    if (!normalized) {
        return null
    }

    const parsed = Number.parseInt(normalized, 10)
    if (!Number.isFinite(parsed) || parsed < 0) {
        return null
    }

    return parsed
}

function CashFundCountDialog({
    open,
    theoreticalMinor,
    chequeTheoreticalMinor = 0,
    chequeTheoreticalCount = 0,
    currency = "EUR",
    loading = false,
    onCancel,
    onConfirm,
}: CashFundCountDialogProps) {
    const theme = useTheme()
    const { t } = useTranslation()
    const [countedInput, setCountedInput] = useState("0.00")
    const [chequeCountedInput, setChequeCountedInput] = useState("0.00")
    const [chequeCountedCountInput, setChequeCountedCountInput] = useState("0")

    useEffect(() => {
        if (!open) {
            return
        }
        setCountedInput((Number(theoreticalMinor || 0) / 100).toFixed(2))
        setChequeCountedInput(
            (Number(chequeTheoreticalMinor || 0) / 100).toFixed(2)
        )
        setChequeCountedCountInput(String(Number(chequeTheoreticalCount || 0)))
    }, [open, theoreticalMinor, chequeTheoreticalMinor, chequeTheoreticalCount])

    const countedMinor = useMemo(
        () => parseAmountToMinor(countedInput),
        [countedInput]
    )
    const chequeCountedMinor = useMemo(
        () => parseAmountToMinor(chequeCountedInput),
        [chequeCountedInput]
    )
    const chequeCountedCount = useMemo(
        () => parseCountToInteger(chequeCountedCountInput),
        [chequeCountedCountInput]
    )

    const discrepancyMinor =
        countedMinor == null
            ? null
            : countedMinor - Number(theoreticalMinor || 0)
    const chequeDiscrepancyMinor =
        chequeCountedMinor == null
            ? null
            : chequeCountedMinor - Number(chequeTheoreticalMinor || 0)
    const chequeDiscrepancyCount =
        chequeCountedCount == null
            ? null
            : chequeCountedCount - Number(chequeTheoreticalCount || 0)

    const canValidate =
        countedMinor != null &&
        chequeCountedMinor != null &&
        chequeCountedCount != null &&
        !loading

    const handleConfirm = async () => {
        if (countedMinor == null) {
            return
        }
        await onConfirm(countedMinor)
    }

    return (
        <Dialog
            open={open}
            onClose={loading ? undefined : onCancel}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2.5,
                    border: `1px solid ${alpha(
                        theme.palette.primary.main,
                        0.2
                    )}`,
                    boxShadow: "0 14px 30px rgba(15, 23, 42, 0.14)",
                    overflow: "hidden",
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontWeight: 800,
                    color: theme.palette.primary.dark,
                    borderBottom: "1px solid #f1f5f9",
                }}
            >
                {t("cashFundCountDialog.title")}
            </DialogTitle>
            <DialogContent sx={{ py: 2.5 }}>
                <Box
                    sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "1fr", md: "1fr 1.25fr" },
                        gap: 1.5,
                    }}
                >
                    <Box
                        sx={{
                            border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.18
                            )}`,
                            borderRadius: 2,
                            p: 2.25,
                        }}
                    >
                        <Typography
                            sx={{ fontWeight: 800, mb: 1, color: "#334155" }}
                        >
                            {t("cashFundCountDialog.cashTitle")}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: "0.9rem",
                                color: "#4b5563",
                                mb: 0.5,
                            }}
                        >
                            {t("cashFundCountDialog.theoretical")}
                            {": "}
                            {formatCentsWithSymbol(theoreticalMinor, currency)}
                        </Typography>
                        <TextField
                            fullWidth
                            type="number"
                            label={String(t("cashFundCountDialog.cashCounted"))}
                            value={countedInput}
                            onChange={(event) =>
                                setCountedInput(event.target.value)
                            }
                            inputProps={{ min: 0, step: "0.01" }}
                            sx={{
                                mt: 1.5,
                                "& .MuiOutlinedInput-root": {
                                    borderRadius: 1.75,
                                    backgroundColor: "#ffffff",
                                },
                            }}
                        />
                        <Typography
                            sx={{
                                mt: 1.5,
                                py: 0.75,
                                px: 1.25,
                                borderRadius: 1.25,
                                fontWeight: 800,
                                backgroundColor:
                                    discrepancyMinor == null
                                        ? "#f8fafc"
                                        : discrepancyMinor === 0
                                        ? "#ecfdf5"
                                        : discrepancyMinor > 0
                                        ? "#fffbeb"
                                        : "#fef2f2",
                                color:
                                    discrepancyMinor == null
                                        ? "#6b7280"
                                        : discrepancyMinor === 0
                                        ? "#047857"
                                        : discrepancyMinor > 0
                                        ? "#b45309"
                                        : "#b91c1c",
                            }}
                        >
                            {t("cashFundCountDialog.discrepancy")}
                            {": "}
                            {discrepancyMinor == null
                                ? "-"
                                : formatCentsWithSymbol(
                                      discrepancyMinor,
                                      currency
                                  )}
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            border: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.18
                            )}`,
                            borderRadius: 2,
                            p: 2.25,
                            backgroundColor: "#ffffff",
                        }}
                    >
                        <Typography
                            sx={{ fontWeight: 800, mb: 1, color: "#334155" }}
                        >
                            {t("cashFundCountDialog.chequeTitle")}
                        </Typography>

                        <Typography
                            sx={{ fontSize: "0.9rem", color: "#4b5563", mb: 1 }}
                        >
                            {t("cashFundCountDialog.chequeTheoretical")}{" "}
                            {formatCentsWithSymbol(
                                chequeTheoreticalMinor,
                                currency
                            )}{" "}
                            / {Number(chequeTheoreticalCount || 0)}
                        </Typography>

                        <Box
                            sx={{
                                display: "grid",
                                gridTemplateColumns: {
                                    xs: "1fr",
                                    sm: "1fr 160px",
                                },
                                gap: 1,
                            }}
                        >
                            <TextField
                                fullWidth
                                type="number"
                                label={String(
                                    t("cashFundCountDialog.chequeCountedAmount")
                                )}
                                value={chequeCountedInput}
                                onChange={(event) =>
                                    setChequeCountedInput(event.target.value)
                                }
                                inputProps={{ min: 0, step: "0.01" }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.75,
                                        backgroundColor: "#ffffff",
                                    },
                                }}
                            />
                            <TextField
                                fullWidth
                                type="number"
                                label={String(
                                    t("cashFundCountDialog.chequeCountedCount")
                                )}
                                value={chequeCountedCountInput}
                                onChange={(event) =>
                                    setChequeCountedCountInput(
                                        event.target.value
                                    )
                                }
                                inputProps={{ min: 0, step: "1" }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 1.75,
                                        backgroundColor: "#ffffff",
                                    },
                                }}
                            />
                        </Box>

                        <Typography
                            sx={{
                                mt: 1.5,
                                py: 0.75,
                                px: 1.25,
                                borderRadius: 1.25,
                                fontWeight: 800,
                                backgroundColor:
                                    chequeDiscrepancyMinor == null ||
                                    chequeDiscrepancyCount == null
                                        ? "#f8fafc"
                                        : chequeDiscrepancyMinor === 0 &&
                                          chequeDiscrepancyCount === 0
                                        ? "#ecfdf5"
                                        : chequeDiscrepancyMinor > 0 ||
                                          chequeDiscrepancyCount > 0
                                        ? "#fffbeb"
                                        : "#fef2f2",
                                color:
                                    chequeDiscrepancyMinor == null ||
                                    chequeDiscrepancyCount == null
                                        ? "#6b7280"
                                        : chequeDiscrepancyMinor === 0 &&
                                          chequeDiscrepancyCount === 0
                                        ? "#047857"
                                        : chequeDiscrepancyMinor > 0 ||
                                          chequeDiscrepancyCount > 0
                                        ? "#b45309"
                                        : "#b91c1c",
                            }}
                        >
                            {t("cashFundCountDialog.chequeDiscrepancy")}{" "}
                            {chequeDiscrepancyMinor == null
                                ? "-"
                                : formatCentsWithSymbol(
                                      chequeDiscrepancyMinor,
                                      currency
                                  )}{" "}
                            /{" "}
                            {chequeDiscrepancyCount == null
                                ? "-"
                                : chequeDiscrepancyCount}
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1.25 }}>
                <Button
                    onClick={onCancel}
                    disabled={loading}
                    variant="outlined"
                    color="inherit"
                    sx={{
                        minWidth: 120,
                        borderColor: "#d1d5db",
                        backgroundColor: "#ffffff",
                        fontWeight: 700,
                    }}
                >
                    {t("common.cancel")}
                </Button>
                <Button
                    onClick={handleConfirm}
                    disabled={!canValidate}
                    variant="contained"
                    sx={{ minWidth: 170, fontWeight: 800 }}
                >
                    {t("cashFundCountDialog.validate")}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default CashFundCountDialog
